import React from 'react';
import { Outlet } from 'react-router-dom';
import { styled, Paper } from '@mui/material';
import Header from '../../components/member/shared/Header';
import Footer from '../../components/member/shared/Footer';
import HomeSnackbar from '../../components/home/HomeSnackbar';
import { LAYOUT_CONSTANTS } from '../../theme';

const PREFIX = 'BaseLayout';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'block',
    overflow: 'auto',
    flex: '0 1 100%',
    borderRadius: 0,

    ['& header']: {
      display: 'flex',
      height: theme.spacing(LAYOUT_CONSTANTS.HEADER_HEIGHT),
      paddingLeft: theme.spacing(3),
      alignItems: 'center',
      alignSelf: 'stretch',
      background: theme.palette.headerBackground.main,
      mixBlendMmode: 'multiply',
      boxShadow: theme.shadows[3],

      ['& nav']: {
        display: 'flex',
        paddingRight: theme.spacing(3),
        alignItems: 'center',

        ['& a']: {
          display: 'inline-flex',
          padding: theme.spacing(1.5),
          color: theme.palette.navigationTextColor.main,
          alignItems: 'center',
          ...theme.typography.body1,
          fontWeight: theme.typography.h5.fontWeight,
          textDecoration: 'none',

          ['&:hover']: {
            color: theme.palette.navigationTextHoverColor.main,
          },

          ['&.active']: {
            color: theme.palette.navigationTextActiveColor.main,
          },

          ['& svg']: {
            padding: theme.spacing(1.5),
          },
        },

        ['& .avatar-section']: {
          paddingLeft: theme.spacing(3),
          display: 'flex',
          gap: theme.spacing(0.5),
          backgroundColor: 'transparent',
          border: 'none',

          ['& svg']: {
            fill: theme.palette.navigationTextColor.main,
          },
        },
      },

      ['& .skip-content-link']: {
        position: 'absolute',
        left: '-9999px',

        ['&:focus']: {
          position: 'static',
        },
      },
    },

    ['& footer']: {
      display: 'flex',
      padding: 0,
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      height: theme.spacing(LAYOUT_CONSTANTS.FOOTER_HEIGHT),
      alignItems: 'center',
      alignSelf: 'stretch',
      background: theme.palette.footerBackground.main,
      position: 'relative',
      zIndex: 10,

      ['& .logo']: {
        width: '10%',
        minWidth: '178px',
        objectFit: 'contain',
      },

      ['& .links']: {
        display: 'flex',
        paddingRight: theme.spacing(3),
        alignItems: 'flex-start',
        gap: theme.spacing(6),

        ['& a']: {
          color: theme.palette.footerTextColor.main,
          ...theme.typography.body1,
          textDecoration: 'none',
          ['&:hover']: {
            textDecoration: 'underline',
            cursor: 'pointer',
          },
        },
      },

      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(6.625),
        height: 'auto',
        alignItems: 'flex-start',
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        paddingLeft: theme.spacing(3),

        ['& .spacer']: {
          display: 'none',
        },

        ['& .links']: {
          flexDirection: 'column',
          gap: theme.spacing(2),
        },
      },
    },

    ['& .spacer']: {
      flex: 1,
    },
  },

  [`& .${classes.content}`]: {
    margin: theme.spacing(2),
  },
}));

interface BaseLayoutProps {
  children?: React.ReactNode;
}

function BaseLayout({ children }: BaseLayoutProps) {
  return (
    <Root className={classes.container}>
      <Header />
      <HomeSnackbar />
      {children || <Outlet />}
      <Footer />
    </Root>
  );
}

export default BaseLayout;
